import type { VirtualStagingOptions } from './stagingTypes';

/**
 * Caption-based staging helpers, ported from the design prototype
 * (hifi/ma-gallery.jsx) and mapped onto the backend's option values.
 * Captions only pre-fill the modal's room type — they do not gate which
 * photos can be staged.
 */

/**
 * Mirrors the verified backend constants (VirtualStagingServiceImpl on
 * asset-service origin/develop). The live options endpoint is the runtime
 * source of truth; these only cover the request-failed case so the modal
 * stays usable.
 */
export const FALLBACK_STAGING_OPTIONS: VirtualStagingOptions = {
  roomTypes: [
    'living',
    'bed',
    'kitchen',
    'dining',
    'home_office',
    'outdoor',
    'kids_room'
  ],
  styles: [
    'standard',
    'modern',
    'scandinavian',
    'industrial',
    'midcentury',
    'luxury',
    'farmhouse',
    'coastal'
  ],
  defaultStyle: 'scandinavian'
};

const OUTDOOR = /(exterior|front|rear|back|yard|outdoor|patio|porch|deck)/;
const HOME_OFFICE = /(office|study|den)/;
const KIDS_ROOM = /(kids|nursery|playroom)/;

/**
 * Pre-fill the modal's room type from the caption; 'living' when ambiguous
 * (matches the prototype's fallback — the user can correct it before
 * generating).
 */
export function roomTypeFromCaption(caption?: string): string {
  const c = (caption ?? '').trim().toLowerCase();
  if (OUTDOOR.test(c)) return 'outdoor';
  if (c.includes('dining')) return 'dining';
  if (c.includes('bed')) return 'bed';
  if (c.includes('kitchen')) return 'kitchen';
  if (HOME_OFFICE.test(c)) return 'home_office';
  if (KIDS_ROOM.test(c)) return 'kids_room';
  return 'living';
}

const ROOM_TYPE_LABELS: Record<string, string> = {
  living: 'Living Room',
  bed: 'Bedroom',
  kitchen: 'Kitchen',
  dining: 'Dining Room',
  home_office: 'Home Office',
  outdoor: 'Outdoor',
  kids_room: 'Kids Room'
};

const STYLE_LABELS: Record<string, string> = {
  standard: 'Standard',
  modern: 'Modern',
  scandinavian: 'Scandinavian',
  industrial: 'Industrial',
  midcentury: 'Mid-Century',
  luxury: 'Luxury',
  farmhouse: 'Farmhouse',
  coastal: 'Coastal'
};

/** Unknown values (the backend may add options) degrade to Title Case of the raw value. */
function titleCaseFromValue(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function roomTypeLabel(value: string): string {
  return ROOM_TYPE_LABELS[value] ?? titleCaseFromValue(value);
}

export function styleLabel(value: string): string {
  return STYLE_LABELS[value] ?? titleCaseFromValue(value);
}
